import { CheckCircleOutlineOutlined, DoDisturbOnOutlined, DoNotDisturbOnOutlined, FastForwardOutlined, MoreHoriz, MotionPhotosAutoOutlined, PersonAddOutlined, PersonOutlineOutlined, RemoveCircleOutline, RequestQuote, SpaOutlined, ThumbDownOffAlt, ThumbUpOffAlt } from "@mui/icons-material";
import { ListItemButton, ListItemIcon, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Tooltip, Typography } from "@mui/material";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { Card } from "randmarcomps";
import withManufacturer from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Opportunity, OpportunityDetail } from "../../stores/opportunities/Opportunity";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import EditProductFromOpportunity from "../products/EditProductFromOpportunity";
import ProductDetailsModal from "../products/ProductDetailsModal";
import ButtonWithActions from "../util/ButtonWithActions";
import ButtonWithDialogConfirm from "../util/ButtonWithDialogConfirm";
import CurrencyUtils from "../util/CurrencyUtils";
import { FormatDateFromString } from "../util/DateTimeUtil";

interface IProps {
  store?: RootStore;
  defaultRowsPerPage?: number;
  applicationId: string;
  opportunityDetails?: OpportunityDetail[];
  productOpportunities?: Opportunity[];
  product?: Product;
}

const OpportunityDetailsGrid: React.FC<IProps> = (props) => {
  const { opportunityDetails, productOpportunities, applicationId, product, store, defaultRowsPerPage } = props;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage || 20);

  const handleStateIcon = (state: string) => {
    if (state === "Active") return <CheckCircleOutlineOutlined color="success" />;
    if (state === "Discontinued") return <DoNotDisturbOnOutlined color="error" />;
    if (state === "Hold") return <DoDisturbOnOutlined color="error" />;
    else return <RequestQuote color="info" />;
  };

  const handleChangePage = React.useCallback((_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleConfirmProductOpportunityDeletion = React.useCallback(async (opportunityNumber: string, randmarSKU: string) => {
    const { opportunityStore, snackStore, productStore } = store!;

    if (opportunityNumber === "") return;

    const success = await opportunityStore.deleteProductFromOpportunity(applicationId, opportunityNumber, randmarSKU || "");
    snackStore.showResult(success);

    opportunityStore.getOpportunity(applicationId, opportunityNumber || "", true);
    productStore.getProduct(applicationId, randmarSKU || "");
  }, [store, applicationId]);

  const { categoriesStore: { categories }, userStore } = store!;
  const isSuperAdmin = userStore.isSuperAdmin();

  if (opportunityDetails && opportunityDetails.length > 0) {
    return (
      <Card className="w-full">
        <div className="w-full overflow-auto rounded-[20px]">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ borderColor: "#582c83" }} />
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Category</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>MPN</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Price</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Type</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }} />
                <TableCell align="center" sx={{ borderColor: "#582c83" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(opportunityDetails || [])
                .slice()
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"Opportunity" + index}>

                    <TableCell align="center">
                      <div className="flex flex-row justify-center items-center text-center space-x-1">
                        <div title="Auto update">
                          {row.product?.autoUpdate ? <MotionPhotosAutoOutlined color="success" /> : <MotionPhotosAutoOutlined color="error" />}
                        </div>
                        <div title="State">
                          {handleStateIcon(row.product?.state || "")}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell align="center">
                      <Link to={Path.category(applicationId, row.product?.category)} style={{ marginRight: "10px" }}>
                        {categories.find(c => c.categoryCode === row.product?.category)?.categoryName}
                      </Link>
                    </TableCell>
                    <TableCell align="center">
                      <Link to={Path.product(applicationId, row.randmarSKU)} style={{ marginRight: "10px" }}>
                        {row.product?.mpn}
                      </Link>
                    </TableCell>
                    <TableCell align="center"><FormattedNumber value={row.bidPrice || 0} {...CurrencyUtils.format} /></TableCell>
                    <TableCell align="center">{row.bidPriceType}</TableCell>
                    <TableCell align="center"><ProductDetailsModal randmarSKU={row.randmarSKU || ""} /></TableCell>
                    <TableCell align="center">
                      <ButtonWithActions size="small" icon={<MoreHoriz />}>
                        <EditProductFromOpportunity product={row.product || product!} opportunityDetail={row}>
                          <ListItemButton>
                            <ListItemIcon>
                              <SpaOutlined fontSize="small" />
                            </ListItemIcon>
                            <Typography variant="body2" sx={{ ml: -2 }}>Edit details</Typography>
                          </ListItemButton>
                        </EditProductFromOpportunity>
                        {isSuperAdmin &&
                          <ButtonWithDialogConfirm onConfirmation={() => handleConfirmProductOpportunityDeletion(row.bidNumber || "", (row.product || product)?.randmarSKU || "")} buttonTitle="Delete" text={`Remove opportunity ${row.bidNumber} from ${(row.product || product)?.mpn}`}>
                            <ListItemButton>
                              <ListItemIcon>
                                <RemoveCircleOutline fontSize="small" />
                              </ListItemIcon>
                              <Typography variant="body2" sx={{ ml: -2 }}>Delete</Typography>
                            </ListItemButton>
                          </ButtonWithDialogConfirm>
                        }
                      </ButtonWithActions>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            {(opportunityDetails || []).length > rowsPerPage &&
              <TableFooter>
                <TableRow>
                  <TablePagination
                    count={(opportunityDetails || []).length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>}
          </Table>
        </div>
      </Card>
    );
  }

  if (product && productOpportunities && productOpportunities.length > 0) {
    return (
      <Card className="w-full">
        <div className="w-full overflow-auto rounded-[20px]">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ borderColor: "#582c83" }} />
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>End Date</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Bid Number - Name</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Price</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Type</TableCell>
                <TableCell sx={{ borderColor: "#582c83" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(productOpportunities || [])
                .slice()
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"Opportunity" + index}>
                    <TableCell align="center">
                      <div className="flex flex-row justify-center items-center text-center space-x-1">
                        <div title={(row.active && new Date(row.endDate || "") >= new Date()) ? "Active" : "Inactive"}>
                          {(row.active && new Date(row.endDate || "") >= new Date()) ? <ThumbUpOffAlt color="success" /> : <ThumbDownOffAlt color="error" />}
                        </div>
                        <div title={row.canBeSetAsDefault ? "Can be set as default" : "Cannot be set as default"}>
                          {row.canBeSetAsDefault ? <FastForwardOutlined color="success" /> : <FastForwardOutlined color="error" />}
                        </div>
                        <div title={(row.defaultResellers === "" || row.defaultResellers === null) ? "No default resellers" : row.defaultResellers}>
                          {(row.defaultResellers === "" || row.defaultResellers === null) ? <PersonOutlineOutlined color="error" /> : <PersonAddOutlined color="success" />}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      {FormatDateFromString(row.endDate)}
                    </TableCell>
                    <Tooltip
                      title={<>
                        <div>Start date: {FormatDateFromString(row.startDate)}</div>
                        <div>Last update: {FormatDateFromString(row.lastUpdate)}</div>
                        {row.description && <div>Description: {row.description}</div>}
                      </>}
                      placement="top">
                      <TableCell align="center">
                        <Link to={Path.opportunity(applicationId, row.bidNumber)} style={{ marginRight: "10px" }}>
                          {row.bidNumber} - {row.opportunity}
                        </Link>
                      </TableCell>
                    </Tooltip>
                    <TableCell align="center"><FormattedNumber value={row.bidPrice || 0} {...CurrencyUtils.format} /></TableCell>
                    <TableCell align="center">{row.bidPriceType}</TableCell>
                    <TableCell align="center">
                      <ButtonWithActions size="small" icon={<MoreHoriz />}>
                        <EditProductFromOpportunity product={product} opportunityDetail={row}>
                          <ListItemButton>
                            <ListItemIcon>
                              <SpaOutlined fontSize="small" />
                            </ListItemIcon>
                            <Typography variant="body2" sx={{ ml: -2 }}>Edit details</Typography>
                          </ListItemButton>
                        </EditProductFromOpportunity>
                        {isSuperAdmin &&
                          <ButtonWithDialogConfirm onConfirmation={() => handleConfirmProductOpportunityDeletion(row.bidNumber || "", product.randmarSKU || "")} buttonTitle="Delete" text={`Remove opportunity ${row.bidNumber} from ${product?.mpn}`}>
                            <ListItemButton>
                              <ListItemIcon>
                                <RemoveCircleOutline fontSize="small" />
                              </ListItemIcon>
                              <Typography variant="body2" sx={{ ml: -2 }}>Delete</Typography>
                            </ListItemButton>
                          </ButtonWithDialogConfirm>
                        }
                      </ButtonWithActions>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            {(productOpportunities || []).length > rowsPerPage &&
              <TableFooter>
                <TableRow>
                  <TablePagination
                    count={(productOpportunities || []).length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>}
          </Table>
        </div>
      </Card>
    );
  }

  return null;
};

export default withManufacturer(inject("store")(observer(OpportunityDetailsGrid)));
